import { jsx } from '@amazon/vinyl-tsx'
import type { Maybe } from '@amazon/vinyl-util'
import { AsyncComponentLoader } from '@/components/AsyncComponentLoader'

/**
 * Props for LazyComponent.
 */
export interface LazyComponentProps<M, P> {
    /**
     * The dynamic import that resolves the module containing the component,
     * e.g. `() => import('@/components/ConfirmModal')`.
     */
    readonly load: () => Promise<M>

    /**
     * Creates the component element from the loaded module and the
     * pass-through props.
     */
    readonly create: (module: M, props: P) => HTMLElement

    /**
     * Statically typed pass-through props handed to `create` once the
     * module resolves.
     */
    readonly props: P

    /**
     * Optional element shown while the module loads.
     */
    readonly fallback?: Maybe<HTMLElement>
}

/**
 * Lazy-loads a module and renders a component from it with statically typed
 * pass-through props. Thin sugar over `AsyncComponentLoader` so call sites
 * declare the import, the factory, and the props in one place instead of
 * wiring `loadData` / `createComponent` closures by hand.
 *
 * ```tsx
 * <LazyComponent
 *     load={() => import('@/components/ConfirmModal')}
 *     create={({ ConfirmModal }, p) => <ConfirmModal {...p} />}
 *     props={{ deps }}
 * />
 * ```
 */
export function LazyComponent<M, P>({ load, create, props, fallback }: LazyComponentProps<M, P>) {
    return (
        <AsyncComponentLoader
            loadData={load}
            createComponent={(module) => create(module, props)}
            loadingIndicator={fallback}
        />
    )
}
